/**
 * The gestures that drive the view, and the DOM they act on.
 *
 * `view.ts` is the arithmetic; this is the part that touches the browser. It
 * installs two elements inside the existing stage —
 *
 *     .canvas-stage            (already there, position: relative)
 *       .rcm-viewport          clips, and owns touch
 *         .rcm-view            transformed; holds the canvas and its overlays
 *       .drop-veil             NOT moved
 *       .mc-strip              NOT moved
 *
 * — and the two things left outside are the point. The mode chips and the
 * import veil are chrome: they sit at a corner of the pane and must not slide
 * off it or grow to eight times their size when you zoom in. Only the artwork
 * and the overlays that must line up with it, texel for texel, go inside.
 *
 * The overlays mount later in boot and find their stage through
 * `canvas.parentElement`, so installing this first is what puts them in the
 * right place with no changes to either of them.
 *
 * ## What each input does
 *
 * The awkward constraint is that a mouse and a trackpad are indistinguishable —
 * the browser will not say which one sent a wheel event — so one mapping has to
 * suit both:
 *
 *   plain wheel            pan          (a trackpad's two-finger scroll)
 *   ctrl/⌘ + wheel         zoom         (a trackpad's PINCH arrives exactly
 *                                        like this: browsers force ctrlKey on
 *                                        a pinch, so both land on one path)
 *   one finger / pen       draw         (never pans — the canvas is for drawing)
 *   two fingers            pan + zoom   (together, as one gesture)
 *   space or middle drag   pan          (the desktop idiom, without a tool trip)
 *   Move tool + drag       pan          (the discoverable one)
 */
import {
  clampView,
  fitView,
  type Size,
  transformOf,
  type View,
  viewsDiffer,
  wheelDelta,
  zoomAt,
  zoomFactor,
} from "./view";

const STYLE_ID = "rcm-view-style";

const CSS = `
.rcm-viewport {
  position: absolute;
  inset: 0;
  overflow: hidden;
  /*
    The canvas already claims touch so one finger draws rather than scrolls.
    Saying it here as well covers the margin around the artwork, where a
    two-finger gesture would otherwise scroll the page out from under the pane.
  */
  touch-action: none;
  overscroll-behavior: contain;
}
.rcm-view {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
  /* Promoted, because this transform changes on every frame of a pinch. */
  will-change: transform;
}
/*
  Crisp texels once past about 4x.

  The template is 585x559 and the work that needs zoom is per-texel — a seam
  edge, the two-pixel gutter. Smoothed, a magnified texel is a soft blob and
  its boundary, which is the thing being judged, is exactly what is lost.
*/
.rcm-view[data-crisp] canvas { image-rendering: pixelated; }
`;

/** Above this, texels are drawn hard rather than smoothed. */
const CRISP_AT = 4;

export interface ViewControllerOptions {
  /** The artwork canvas. Its parent must be the stage. */
  canvas: HTMLCanvasElement;
  /** Content size in texels. The template, normally. */
  content: Size;
  /**
   * Whether a plain one-pointer drag should pan.
   *
   * True while the Move tool is active. Everything else — space, the middle
   * button, two fingers — pans regardless, because those are gestures rather
   * than modes and interrupting a drawing tool to reach for one is the cost
   * this avoids.
   */
  panning: () => boolean;
  /**
   * A second pointer has landed and the gesture is now a pinch.
   *
   * The stroke that the first finger started has to be abandoned, or a pinch
   * leaves a smear across the artwork — the single most common complaint about
   * web drawing tools on a tablet.
   */
  onGestureTakeover?: () => void;
  onChange?: (view: View) => void;
  doc?: Document;
}

export interface ViewController {
  get(): View;
  /** Set directly; clamped before it lands. */
  set(next: View): void;
  /** Show the whole template, centred. */
  fit(): void;
  /** Multiply the scale, about a screen point or the pane's centre. */
  zoomBy(factor: number, at?: { x: number; y: number }): void;
  /** True while a pan or pinch is in progress, so painting can stand aside. */
  gesturing(): boolean;
  /**
   * Whether this press belongs to a pan rather than to the active tool.
   *
   * Asked BEFORE a pan starts, which is why `gesturing()` cannot answer it:
   * this controller listens on the viewport wrapping the canvas, so a
   * pointerdown reaches the canvas first and nothing has begun yet.
   */
  wantsPan(e: PointerEvent): boolean;
  destroy(): void;
}

export function mountViewController(opts: ViewControllerOptions): ViewController {
  const doc = opts.doc ?? document;
  const canvas = opts.canvas;
  const stage = canvas.parentElement;
  if (!stage) throw new Error("view: the canvas has no stage to sit in");

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const viewport = doc.createElement("div");
  viewport.className = "rcm-viewport";
  const content = doc.createElement("div");
  content.className = "rcm-view";
  content.style.width = `${opts.content.w}px`;
  content.style.height = `${opts.content.h}px`;

  // In before the canvas moves, so the canvas never leaves the document.
  stage.insertBefore(viewport, canvas);
  viewport.appendChild(content);
  content.appendChild(canvas);

  /*
    The canvas is laid out by the view now, not by the pane.

    It used to size itself with aspect-ratio and object-fit so it fitted
    whatever space it was given. Inside the view it has to be exactly the
    content size, because that is the unit the scale multiplies — anything
    else and one texel is not one unit and every mapping is off by a ratio
    nothing records.
  */
  canvas.style.position = "absolute";
  canvas.style.left = "0";
  canvas.style.top = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.maxWidth = "none";
  canvas.style.maxHeight = "none";

  const port = (): Size => ({ w: viewport.clientWidth, h: viewport.clientHeight });
  let view: View = fitView(opts.content, port());

  function apply(next: View, force = false) {
    const clamped = clampView(next, opts.content, port());
    if (!force && !viewsDiffer(view, clamped)) return;
    view = clamped;
    content.style.transform = transformOf(view);
    if (view.scale >= CRISP_AT) content.dataset.crisp = "";
    else delete content.dataset.crisp;
    opts.onChange?.(view);
  }

  /** Screen coordinates relative to the viewport, which is what view.ts wants. */
  function local(e: { clientX: number; clientY: number }) {
    const r = viewport.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // ---------------------------------------------------------------- wheel

  const onWheel = (e: WheelEvent) => {
    // Both branches take the event: unhandled, the browser zooms the page or
    // scrolls the shell out from under the pane.
    e.preventDefault();
    const { dx, dy } = wheelDelta(e);
    const at = local(e);
    /*
      ctrlKey means pinch, not Ctrl.

      Browsers encode a trackpad pinch as a wheel event with ctrlKey forced
      true — IE10's idea, adopted by Chrome and Firefox and now universal. So
      the pinch and the deliberate ⌘-wheel are the same path, and there is no
      need to know whether a mouse or a trackpad sent it. Which is fortunate:
      the browser does not say.
    */
    if (e.ctrlKey || e.metaKey) apply(zoomAt(view, view.scale * zoomFactor(dy), at.x, at.y));
    else apply({ ...view, x: view.x - dx, y: view.y - dy });
  };
  // Not passive, or preventDefault is ignored and the page zooms instead.
  viewport.addEventListener("wheel", onWheel, { passive: false });

  // -------------------------------------------------------------- pointers

  /** Live pointers, by id. Two of them is a pinch. */
  const pointers = new Map<number, { x: number; y: number }>();
  /** The gesture in progress, or null. */
  let gesture: { midX: number; midY: number; dist: number } | null = null;
  /** A one-pointer pan — space, middle button, or the Move tool. */
  let dragging: { id: number; x: number; y: number } | null = null;
  let spaceHeld = false;

  /**
   * Whether this press belongs to a pan rather than to whatever tool is up.
   *
   * Middle button, or space held, or the Move tool — the three ways to grab the
   * canvas. Exposed rather than kept private because the painter has to ask the
   * same question and there must be one answer: this listener sits on the
   * viewport, a DIV wrapping the canvas, so a pointerdown reaches the canvas
   * FIRST and `preventDefault` does not stop it. Without asking, holding space
   * or picking the Move tool started a stroke and panned at the same time.
   */
  const wantsPan = (e: PointerEvent) => e.button === 1 || spaceHeld || opts.panning();

  /*
    Two points, taken as two arguments.

    These used to take the array and assert `pts[0]!` — true, since they are
    only ever called with exactly two live pointers, but the assertion put the
    precondition in a place the compiler could not check and a reader had to go
    looking for. Naming both makes it the caller's job to have two, which is
    where the knowledge already is.
  */
  type Pt = { x: number; y: number };
  const centreOf = (a: Pt, b: Pt) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const spread = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === "touch") {
      pointers.set(e.pointerId, local(e));
      if (pointers.size === 2) {
        /*
          The first finger has been drawing. Tell the painter to drop that
          stroke before this becomes a pinch, or the zoom leaves a smear.
        */
        opts.onGestureTakeover?.();
        const pts = [...pointers.values()];
        const [a, b] = pts;
        if (!a || !b) return;
        const c = centreOf(a, b);
        gesture = { midX: c.x, midY: c.y, dist: spread(a, b) };
      }
      return;
    }

    if (!wantsPan(e)) return;
    e.preventDefault();
    const p = local(e);
    dragging = { id: e.pointerId, x: p.x, y: p.y };
    /*
      Capture keeps a pan alive when the pointer leaves the pane, which is the
      common case when dragging toward an edge. It throws if the pointer is not
      currently down — a synthetic event, or a real one the browser has already
      taken — and a pan that works is worth more than a captured one, so the
      failure is swallowed rather than allowed to abandon the gesture.
    */
    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      // Not capturable; the listeners on the viewport still track the drag.
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerType === "touch" && pointers.has(e.pointerId)) {
      pointers.set(e.pointerId, local(e));
      if (gesture && pointers.size === 2) {
        const pts = [...pointers.values()];
        const [a, b] = pts;
        if (!a || !b) return;
        const c = centreOf(a, b);
        const d = spread(a, b);
        // Pan by how far the midpoint moved, zoom by how much the fingers
        // spread — applied as one update so they do not fight each other.
        const panned: View = {
          ...view,
          x: view.x + (c.x - gesture.midX),
          y: view.y + (c.y - gesture.midY),
        };
        const factor = gesture.dist > 0 ? d / gesture.dist : 1;
        apply(zoomAt(panned, view.scale * factor, c.x, c.y));
        gesture = { midX: c.x, midY: c.y, dist: d };
      }
      return;
    }

    if (!dragging || e.pointerId !== dragging.id) return;
    const p = local(e);
    apply({ ...view, x: view.x + (p.x - dragging.x), y: view.y + (p.y - dragging.y) });
    dragging = { id: e.pointerId, x: p.x, y: p.y };
  };

  /*
    Up and cancel do the same thing, and handling cancel is not optional.

    The browser fires pointercancel when it takes a gesture over — a system
    edge swipe, a call arriving. Without this the pointer stays in the map
    for ever, so the next single touch is treated as the second half of a
    pinch and the canvas leaps.
  */
  const endPointer = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) gesture = null;
    if (dragging && e.pointerId === dragging.id) {
      dragging = null;
      if (viewport.hasPointerCapture(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
    }
  };

  viewport.addEventListener("pointerdown", onPointerDown);
  viewport.addEventListener("pointermove", onPointerMove);
  viewport.addEventListener("pointerup", endPointer);
  viewport.addEventListener("pointercancel", endPointer);

  // -------------------------------------------------------------- keyboard

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Space" && !e.repeat) {
      const t = e.target as HTMLElement | null;
      // Space is a button press and a text character before it is a pan.
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|BUTTON|SELECT)$/.test(t.tagName))) return;
      spaceHeld = true;
      viewport.style.cursor = "grab";
    }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "Space") {
      spaceHeld = false;
      viewport.style.cursor = "";
    }
  };
  doc.addEventListener("keydown", onKeyDown);
  doc.addEventListener("keyup", onKeyUp);

  // ---------------------------------------------------------------- resize

  /*
    Re-clamp on resize rather than re-fit.

    Re-fitting would throw away a zoom every time the pane divider moved, which
    is a thing people do *while* working at a zoom. Clamping keeps the view and
    only corrects it if the new size made it illegal.
  */
  const observer = new ResizeObserver(() => apply(view, true));
  observer.observe(viewport);

  apply(view, true);

  return {
    get: () => view,
    set: (next) => apply(next),
    fit: () => apply(fitView(opts.content, port()), true),
    zoomBy(factor, at) {
      const p = port();
      const point = at ?? { x: p.w / 2, y: p.h / 2 };
      apply(zoomAt(view, view.scale * factor, point.x, point.y));
    },
    gesturing: () => gesture !== null || dragging !== null,
    /** Whether this press is a pan. See the note on `wantsPan`. */
    wantsPan,
    destroy() {
      observer.disconnect();
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("pointerdown", onPointerDown);
      viewport.removeEventListener("pointermove", onPointerMove);
      viewport.removeEventListener("pointerup", endPointer);
      viewport.removeEventListener("pointercancel", endPointer);
      doc.removeEventListener("keydown", onKeyDown);
      doc.removeEventListener("keyup", onKeyUp);
      // Put the canvas back where it was found, so a re-mount is not a nest.
      stage.insertBefore(canvas, viewport);
      viewport.remove();
    },
  };
}
